import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import PageShell from "@/components/feature/PageShell";
import VideoCard from "@/components/feature/VideoCard";
import Seo from "@/components/base/Seo";
import { videos, videoCategories } from "@/mocks/videos";

export default function Library() {
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get("q") ?? "";
  const category = searchParams.get("category") ?? "全部";
  const [sort, setSort] = useState<"hot" | "new">("hot");
  const [input, setInput] = useState(q);

  useEffect(() => setInput(q), [q]);

  const filtered = useMemo(() => {
    let list = videos;
    if (category !== "全部") {
      list = list.filter((v) => v.category === category);
    }
    if (q) {
      list = list.filter(
        (v) => v.title.includes(q) || v.tags.some((t) => t.includes(q)),
      );
    }
    return sort === "hot"
      ? [...list].sort((a, b) => (a.hot === b.hot ? 0 : a.hot ? -1 : 1))
      : list;
  }, [category, q, sort]);

  const setCategory = (cat: string) => {
    const next = new URLSearchParams(searchParams);
    if (cat === "全部") next.delete("category");
    else next.set("category", cat);
    setSearchParams(next);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const next = new URLSearchParams(searchParams);
    if (input.trim()) next.set("q", input.trim());
    else next.delete("q");
    setSearchParams(next);
  };

  return (
    <PageShell>
      <Seo
        title="视频库 - 娱乐综艺/热剧/搞笑分类在线观看 | 瓜趣视频"
        description="瓜趣视频视频库，按娱乐综艺、明星八卦、热剧剪辑、搞笑日常、社会热点、生活妙招等分类浏览最新热门视频，高清流畅秒开播放，手机端完美适配。"
        keywords="视频库,热门视频分类,娱乐综艺,热剧剪辑,搞笑日常,瓜趣视频"
        canonical="https://gagplay6.github.io/library"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: "瓜趣视频 - 视频库",
            url: "https://gagplay6.github.io/library",
            description: "按分类浏览最新热门视频",
            inLanguage: "zh-CN",
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: "https://gagplay6.github.io/" },
              { "@type": "ListItem", position: 2, name: "视频库", item: "https://gagplay6.github.io/library" },
            ],
          },
        ]}
      />

      <div className="container-x py-8">
        <nav className="flex items-center gap-1.5 text-sm text-foreground-500" aria-label="面包屑导航">
          <Link to="/" className="hover:text-primary-600">
            首页
          </Link>
          <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
          <span className="text-foreground-700">视频库</span>
        </nav>

        <div className="mt-4 flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
          <h1 className="font-heading text-2xl text-foreground-950 md:text-3xl">
            {category === "全部" ? "全部视频" : `${category}`}
            {q && <span className="ml-2 text-base text-foreground-500">搜索“{q}”的结果</span>}
          </h1>
          <form
            onSubmit={handleSearch}
            className="flex items-center overflow-hidden rounded-md border border-background-300 bg-background-100 focus-within:border-primary-400"
            role="search"
          >
            <i className="ri-search-line pl-3 text-sm text-foreground-400" aria-hidden="true"></i>
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="搜索视频..."
              aria-label="搜索视频"
              className="w-44 bg-transparent px-2 py-2 text-sm outline-none sm:w-56"
            />
            <button
              type="submit"
              className="whitespace-nowrap bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
            >
              搜索
            </button>
          </form>
        </div>

        <div className="mt-5 flex flex-wrap items-center gap-2">
          {videoCategories.map((cat) => (
            <button
              key={cat}
              type="button"
              onClick={() => setCategory(cat)}
              className={`chip ${
                category === cat
                  ? "bg-primary-500 text-background-50"
                  : "bg-background-100 text-foreground-700 hover:bg-primary-100 hover:text-primary-700"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        <div className="mt-4 flex items-center gap-3">
          <span className="text-sm text-foreground-600">排序：</span>
          <button
            type="button"
            onClick={() => setSort("hot")}
            className={`chip ${
              sort === "hot" ? "bg-secondary-500 text-background-50" : "bg-background-100 text-foreground-700"
            }`}
          >
            <i className="ri-fire-fill" aria-hidden="true"></i>
            热门优先
          </button>
          <button
            type="button"
            onClick={() => setSort("new")}
            className={`chip ${
              sort === "new" ? "bg-secondary-500 text-background-50" : "bg-background-100 text-foreground-700"
            }`}
          >
            <i className="ri-time-line" aria-hidden="true"></i>
            最新上架
          </button>
          <span className="ml-auto text-sm text-foreground-500">共 {filtered.length} 个视频</span>
        </div>

        {filtered.length > 0 ? (
          <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((video) => (
              <VideoCard key={video.id} video={video} />
            ))}
          </div>
        ) : (
          <div className="card-surface mt-6 flex flex-col items-center justify-center py-16 text-center">
            <i className="ri-search-line text-5xl text-foreground-300" aria-hidden="true"></i>
            <p className="mt-4 text-base font-semibold text-foreground-700">没有找到相关视频</p>
            <p className="mt-1 text-sm text-foreground-500">换个关键词或分类试试吧</p>
            <Link to="/library" className="btn-primary mt-5">
              重置筛选
            </Link>
          </div>
        )}
      </div>
    </PageShell>
  );
}